import { captureMessage } from '@sentry/react';
import { tzAxios } from 'api/axios';
import { CheckoutRequest, CalculatePriceRequest } from 'api/requestTypes';
import { CheckoutResponse, CalculatePriceResponse } from 'api/responseTypes';
import { parseSavedHolders, type ParsedSavedHolders } from './savedHolders';

export const postCheckout = async (payload: CheckoutRequest) =>
  (await tzAxios.post<CheckoutResponse>(`/ticket-zone/checkout`, payload)).data;

export const calculatePrice = async (
  payload: CalculatePriceRequest,
): Promise<CalculatePriceResponse> =>
  (await tzAxios.post<CalculatePriceResponse>(`/ticket-zone/checkout/calculate-price`, payload))
    .data;

/**
 * The buyer's own contact (`me`) plus the holders they have booked for before.
 *
 * Fetched once when the checkout page loads: `me` auto-assigns ticket 1, and the cached list feeds
 * the saved-holders sheet for every other ticket (TZ-CO-5).
 *
 * Resolves against `VITE_APP_BASE_API` (which already ends in `/consumers`) to
 * `…/consumers/ticket-zone/tickets/saved-holders`. Rejections are left to propagate so the caller
 * can read the status — `useSavedHolders` turns them into an empty result, and a `401` into re-auth.
 *
 * Dropped rows are reported to Sentry. Without that, a response whose shape drifts from the
 * contract degrades to an empty sheet, which is indistinguishable from "this buyer has no saved
 * holders" — silent, and most likely exactly when the endpoint is first integrated. That is not
 * hypothetical: the documented `sh_` id prefix did not match the live numeric id, and this warning
 * would have been the only sign.
 */
export const getSavedHolders = async (): Promise<Omit<ParsedSavedHolders, 'droppedCount'>> => {
  const { data } = await tzAxios.get<unknown>(`/ticket-zone/tickets/saved-holders`);
  const { me, holders, droppedCount } = parseSavedHolders(data);

  if (droppedCount > 0) {
    captureMessage('saved-holders response did not match the contract', {
      level: 'warning',
      extra: { droppedCount, keptCount: holders.length },
    });
  }

  return { me, holders };
};
